import React, {
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
  memo,
} from 'react';
import { useRouter } from 'next/router';
import {
  IS_INTERRACTIVE_ADS_ON,
  IS_STORY_GAME_ADS_ON,
  IS_WEB_NOVEL_ADS_ON,
  STORAGE_KEY,
} from '@common/values';
import { adStart, adFinish } from '@lib/eventManager';
import { isDevelopment } from '@/common/utils';
import { Challenge_Story_Genre, Story_Play_Type } from '@/baseType';
import WebToAppWarningPopup from './WebToAppWarningPopup';

declare global {
  interface Window {
    googletag: any;
  }
}

interface IAdvertisementPopup {
  onLoading: Dispatch<SetStateAction<boolean>>;
  fallbackUrl?: string;
  isShow?: boolean;
  onFinish: () => void;
  startChapterTransaction?: any;
  chapterData: {
    storyId: number;
    storyName: string;
    chapterId: number;
    chapterIndex: number;
    storyBundleId?: number;
    storyLanguage?: string;
    storyGenre?: Challenge_Story_Genre;
    playType: Story_Play_Type;
  };
  isPurchasableStory: boolean; // 통구매 작품인가?
  rewardType: 'FREE_CHAPTER' | 'FREE_CHARACTER';
}

// 테스트용: '/22639388115/rewarded_web_example'
// 운영용: '/22802458718/start_chapter_reward'
const AD_UNIT_ID = isDevelopment()
  ? '/22639388115/rewarded_web_example'
  : '/22802458718/start_chapter_reward';

const AdvertisementPopup = ({
  onLoading,
  onFinish,
  fallbackUrl,
  isShow,
  startChapterTransaction,
  chapterData,
  rewardType,
}: IAdvertisementPopup) => {
  const router = useRouter();
  const rewardedAdRef = useRef<any>();
  const intervalRef = useRef<NodeJS.Timeout>();
  const setTimeoutRef = useRef<NodeJS.Timeout>();
  const isRewardedSlotReadyCallRef = useRef<boolean>(false);
  const isRewardedSlotGrantedCallRef = useRef<boolean>(false);
  const viewAdvertisementSpan = useRef<any>();
  const [isWebToappPopupOpen, setIsWebToappPopupOpen] = useState(false);

  const removeSlot = () => {
    viewAdvertisementSpan?.current?.finish();

    const googletag = window.googletag || { cmd: [] };
    window.googletag = googletag;

    googletag.cmd.push(() => {
      googletag.destroySlots([rewardedAdRef.current]);
    });
  };

  useEffect(() => {
    if (isShow !== undefined) {
      setTimeout(() => {
        // 광고가 보여지고있지 않은 경우에만 광고 finish
        if (!isRewardedSlotReadyCallRef.current) {
          onFinish();
          onLoading(false);
        }
      }, 2000);
    }
  }, [isShow]);

  function isAdsOffForPlayType(
    playType: Story_Play_Type,
    adsOnFlag: boolean,
  ): boolean {
    return chapterData.playType === playType && !adsOnFlag;
  }

  useEffect(() => {
    if (!isShow) return;

    const isStoryGameAdsOff = isAdsOffForPlayType(
      Story_Play_Type.Storygame,
      IS_STORY_GAME_ADS_ON,
    );
    const isInteractiveAdsOff = isAdsOffForPlayType(
      Story_Play_Type.Interactive,
      IS_INTERRACTIVE_ADS_ON,
    );
    const isWebNovelAdsOff = isAdsOffForPlayType(
      Story_Play_Type.Epub,
      IS_WEB_NOVEL_ADS_ON,
    );

    const shouldShowPopup =
      isStoryGameAdsOff || isInteractiveAdsOff || isWebNovelAdsOff;

    if (shouldShowPopup) {
      setIsWebToappPopupOpen(true);
      return;
    }

    if (startChapterTransaction) {
      viewAdvertisementSpan.current = startChapterTransaction?.startChild({
        op: 'ViewAdvertisement',
        describe: '리워드 광고 시작',
      });
    }

    onLoading(true);

    const googletag = window.googletag || { cmd: [] };
    window.googletag = googletag;

    googletag.cmd.push(() => {
      rewardedAdRef.current = googletag.defineOutOfPageSlot(
        AD_UNIT_ID,
        googletag.enums.OutOfPageFormat.REWARDED,
      );

      if (rewardedAdRef.current) {
        rewardedAdRef.current.addService(googletag.pubads());

        googletag
          .pubads()
          .addEventListener('rewardedSlotReady', (event: any) => {
            if (isRewardedSlotReadyCallRef.current) return;

            adStart({
              storyId: chapterData.storyId,
              adUnitId: AD_UNIT_ID,
              adLoc:
                chapterData.playType === Story_Play_Type.Interactive
                  ? '회차시작-인터랙티브_리워드'
                  : chapterData.playType === Story_Play_Type.Epub
                  ? '회차시작-웹소설_리워드'
                  : '회차시작-스토리게임_리워드',
              adUrl: null,
              storyBundleId: chapterData.storyBundleId,
              chapterId: chapterData.chapterId,
              chapterIndex: chapterData.chapterIndex,
              storyName: chapterData.storyName,
              storyLanguage: chapterData.storyLanguage,
              storyGenre: chapterData.storyGenre,
              playType:
                chapterData.playType === Story_Play_Type.Interactive
                  ? 'interactive'
                  : chapterData.playType === Story_Play_Type.Epub
                  ? 'novel'
                  : 'storygame',
              rewardType: rewardType,
            });
            onLoading(false);

            setTimeoutRef.current && clearTimeout(setTimeoutRef.current);
            event.makeRewardedVisible();
            isRewardedSlotReadyCallRef.current = true;
          });

        googletag.pubads().addEventListener('rewardedSlotClosed', () => {
          removeSlot();
          router.push(fallbackUrl || '/');
        });

        googletag.pubads().addEventListener('rewardedSlotGranted', () => {
          onFinish();

          if (isRewardedSlotGrantedCallRef.current) return;
          isRewardedSlotGrantedCallRef.current = true;

          adFinish({
            storyId: chapterData.storyId,
            adUnitId: AD_UNIT_ID,
            adLoc:
              chapterData.playType === Story_Play_Type.Interactive
                ? '작품상세-인터랙티브_리워드'
                : chapterData.playType === Story_Play_Type.Epub
                ? '작품상세-웹소설_리워드'
                : '작품상세-스토리게임_리워드',
            adUrl: null,
            storyBundleId: chapterData.storyBundleId,
            chapterId: chapterData.chapterId,
            chapterIndex: chapterData.chapterIndex,
            storyName: chapterData.storyName,
            storyLanguage: chapterData.storyLanguage,
            storyGenre: chapterData.storyGenre,
            playType:
              chapterData.playType === Story_Play_Type.Interactive
                ? 'interactive'
                : chapterData.playType === Story_Play_Type.Epub
                ? 'novel'
                : 'storygame',
            rewardType: rewardType,
          });
          sessionStorage.setItem(
            STORAGE_KEY.WATCHED_AD_CHAPTER_ID,
            String(chapterData.chapterId),
          );
          googletag.destroySlots([rewardedAdRef.current]);
        });

        googletag.enableServices();
        googletag.display(rewardedAdRef.current);
      }
    });

    intervalRef.current = setInterval(() => {
      googletag.pubads().refresh([rewardedAdRef.current]);
    }, 500);

    setTimeoutRef.current = setTimeout(() => {
      intervalRef.current && clearInterval(intervalRef.current);
    }, 2000);

    return () => {
      intervalRef.current && clearInterval(intervalRef.current);
      setTimeoutRef.current && clearTimeout(setTimeoutRef.current);
    };
  }, [isShow]);

  return (
    <>
      {isWebToappPopupOpen && (
        <WebToAppWarningPopup
          onClose={() => {
            router.push(`/story/${chapterData.storyId}`);
            setIsWebToappPopupOpen(false);
          }}
          storyId={chapterData.storyId}
        />
      )}
    </>
  );
};

export default memo(AdvertisementPopup);
